import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Popper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined'
import Dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import React from 'react'

import {
  confirmReceipt,
  fetchMyCentres,
  fetchProgramMaterial,
  fetchProgramMaterialHistory,
  saveProgramMaterial,
  searchCatalogue,
  uploadItemImage
} from 'api/procurement'
import constants from 'app/constants'
import { confirm, eventAlert, notify } from 'app/helpers'

import AttachmentUploader from '../components/AttachmentUploader'
import BudgetBanner from '../components/BudgetBanner'
import { errorText } from '../components/errorText'
import { downloadCsv, parseCsvFile } from '../components/exportUtils'
import {
  checkOverallBudget,
  overBudgetMessage,
  softOverspendNote
} from '../components/overallBudget'
import { SHOW_CSV_IMPORT, defaultOrderMonth, orderCentres } from '../constants'
import SopButton from '../components/SopButton'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  // Page-level sticky header (2026-07-21): headers freeze at the top of the
  // screen on scroll-down for long order-line lists.
  stickyHead: {
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 5,
      background: theme.palette.grey[100],
      boxShadow: 'inset 0 -1px 0 #e0ddd6'
    }
  },
  // Hide number-input spinner arrows (error-prone; could wipe values).
  noSpin: {
    '& input[type=number]': { MozAppearance: 'textfield' },
    '& input[type=number]::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0
    }
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  spacer: { flexGrow: 1 },
  select: { minWidth: 180 },
  status: { textTransform: 'lowercase', fontWeight: 600 },
  total: { fontWeight: 700 },
  searchBox: { position: 'relative' },
  dropdown: {
    position: 'absolute',
    zIndex: 30,
    left: 0,
    right: 0,
    marginTop: 2,
    maxHeight: 280,
    overflowY: 'auto'
  },
  optMain: { whiteSpace: 'normal', flex: 1, marginRight: 8 },
  optRef: { color: theme.palette.text.secondary, fontSize: 11 },
  optCost: { color: theme.palette.text.secondary, whiteSpace: 'nowrap' },
  meta: { fontSize: 11, color: theme.palette.text.secondary, marginTop: 2 },
  viewLink: { fontWeight: 600, marginRight: 8 },
  lineChip: { marginTop: 6 },
  lineNote: { fontSize: 11, color: theme.palette.text.secondary, marginTop: 2 }
}))

const num = (v) => {
  const n = Number(v)
  return Number.isNaN(n) ? 0 : n
}

const emptyLine = () => ({
  item_name: '',
  qty: '',
  unit_cost: '',
  remarks: '',
  reference_link: '',
  // true when the link came from the catalogue (shown read-only). When the
  // catalogue has no link, the centre supplies a link or an image (gap line).
  link_from_catalogue: false,
  reference_image: '', // S3 key, set after an upload; saved with the line
  reference_image_url: '', // short-lived view URL for the uploaded image
  unit: '',
  reference_id: '',
  is_catalogue: false,
  uuid: '',
  line_status: 'pending',
  line_note: ''
})

// Order-status labels — "sent" reads as "Sent to centre" so the story is clear.
const ORDER_STATUS_LABEL = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  sent: 'Sent to centre',
  received: 'Received',
  fulfilled: 'Fulfilled'
}

const LINE_STATUS_LABEL = {
  sent: 'Sent',
  unavailable: 'Not available'
}
const LINE_STATUS_STYLE = {
  sent: { background: '#e8f5e9', color: '#2e7d32' },
  unavailable: { background: '#ffebee', color: '#c62828' }
}

// Per-line catalogue search. The box is a pure FILTER — an item is committed to
// the line only when the centre picks a catalogue result (onPick). Typed text
// that isn't picked never becomes an orderable line, so nothing outside the
// catalogue can be ordered. The dropdown renders in a Popper (portal) so the
// horizontally-scrollable line table can't clip it.
function CatalogueSearch({ line, onPick, disabled }) {
  const classes = useStyles()
  const [open, setOpen] = React.useState(false)
  const [results, setResults] = React.useState([])
  const [query, setQuery] = React.useState(line.item_name || '')
  const anchorRef = React.useRef(null)

  // Keep the box in sync when the line's item changes from outside (order load,
  // CSV import, pick) without letting typing leak into the committed item.
  React.useEffect(() => {
    setQuery(line.item_name || '')
  }, [line.item_name])

  const run = React.useCallback(async (text) => {
    try {
      const res = await searchCatalogue(text)
      setResults(res?.results || [])
    } catch (e) {
      setResults([])
    }
  }, [])

  return (
    <div className={classes.searchBox}>
      <TextField
        fullWidth
        inputRef={anchorRef}
        size="small"
        variant="outlined"
        placeholder="Search catalogue…"
        disabled={disabled}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
          run(e.target.value)
        }}
        onFocus={() => {
          if (disabled) return
          setOpen(true)
          run(query)
        }}
        // Revert stray (unpicked) text so it can't masquerade as an item.
        onBlur={() =>
          setTimeout(() => {
            setOpen(false)
            setQuery(line.item_name || '')
          }, 200)
        }
      />
      <Popper
        open={!disabled && open && results.length > 0}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}>
        <Paper
          elevation={4}
          style={{
            width: anchorRef.current ? anchorRef.current.offsetWidth : 240,
            maxHeight: 280,
            overflowY: 'auto'
          }}>
          {results.map((it) => (
            <MenuItem
              key={it.reference_id || it.name}
              onMouseDown={() => {
                onPick(it)
                setQuery(it.name)
                setOpen(false)
              }}
              style={{ display: 'flex', alignItems: 'flex-start' }}>
              <span className={classes.optMain}>
                {it.name}
                {it.reference_id ? (
                  <span className={classes.optRef}> · {it.reference_id}</span>
                ) : null}
              </span>
              <span className={classes.optCost}>
                {it.cost != null ? `₹${it.cost}` : '—'}
              </span>
            </MenuItem>
          ))}
        </Paper>
      </Popper>
      {/* The item link now lives in its own column; here we only note the unit. */}
      {line.unit ? (
        <div className={classes.meta}>
          <span>per {line.unit}</span>
        </div>
      ) : null}
    </div>
  )
}

const ProgramMaterial = ({ centreId: controlledCentreId, initialMonth } = {}) => {
  const classes = useStyles()
  // Centre supplied by the Center Team layout when controlled (picker hidden).
  const controlled = !!controlledCentreId

  const [centres, setCentres] = React.useState([])
  const [centreId, setCentreId] = React.useState(controlledCentreId || '')
  const [month, setMonth] = React.useState(
    initialMonth || defaultOrderMonth()
  )
  const [status, setStatus] = React.useState('draft')
  const [lines, setLines] = React.useState([])
  const [reviewRemarks, setReviewRemarks] = React.useState('')
  const [headerRemarks, setHeaderRemarks] = React.useState('')
  const [budget, setBudget] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [history, setHistory] = React.useState([])
  const [viewOrder, setViewOrder] = React.useState(null)
  const [discrepancy, setDiscrepancy] = React.useState(false)
  const [discrepancyNotes, setDiscrepancyNotes] = React.useState('')
  const [monthFrozen, setMonthFrozen] = React.useState(false)
  // receipt dialog: null = closed; true = open. Per-line receipt rows live in
  // receiptLines: [{ uuid, item_name, receipt_status, receipt_note }].
  const [receipt, setReceipt] = React.useState(null)
  const [receiptLines, setReceiptLines] = React.useState([])
  const [receiptSaving, setReceiptSaving] = React.useState(false)
  // N4: rows whose qty×unit_cost ≠ Line Total in an uploaded CSV (blocks import)
  const [badRows, setBadRows] = React.useState(null)

  const monthDate = `${month}-01`
  // Locked once HQ has acted (status not draft/rejected) OR the whole month has
  // been frozen by HQ.
  const locked = monthFrozen || !['draft', 'rejected'].includes(status)

  const loadHistory = React.useCallback(async () => {
    if (!centreId) return
    try {
      const res = await fetchProgramMaterialHistory({ centre: centreId })
      setHistory(res?.results || [])
    } catch (e) {
      setHistory([])
    }
  }, [centreId])

  React.useEffect(() => {
    loadHistory()
  }, [loadHistory])

  const openView = async (m) => {
    try {
      const res = await fetchProgramMaterial({ centre: centreId, month: m })
      setViewOrder({ month: m, ...res })
    } catch (e) {
      /* ignore */
    }
  }

  React.useEffect(() => {
    if (controlled) {
      setCentreId(controlledCentreId)
      return
    }
    ;(async () => {
      try {
        const res = await fetchMyCentres()
        const list = orderCentres(res?.results || [])
        setCentres(list)
        if (list.length) setCentreId(list[0].uuid)
      } catch (error) {
        eventAlert(
          error?.message || 'Failed to load centres',
          constants.ERROR,
          'Program Material'
        )
      }
    })()
  }, [controlled, controlledCentreId])

  const loadOrder = React.useCallback(async () => {
    if (!centreId) return
    setLoading(true)
    try {
      const res = await fetchProgramMaterial({
        centre: centreId,
        month: monthDate
      })
      setStatus(res.status || 'draft')
      setReviewRemarks(res.review_remarks || '')
      setHeaderRemarks(res.remarks || '')
      setDiscrepancy(!!res.discrepancy)
      setDiscrepancyNotes(res.discrepancy_notes || '')
      setMonthFrozen(!!res.frozen)
      setLines(
        (res.lines || []).map((l) => ({
          item_name: l.item_name,
          qty: l.qty,
          unit_cost: l.unit_cost,
          remarks: l.remarks || '',
          reference_link: l.reference_link || '',
          // A saved line with a link is shown read-only (the catalogue, or a
          // gap the centre already filled); a saved gap with no link stays
          // editable so it can still be completed before resubmitting.
          link_from_catalogue: !!l.reference_link,
          reference_image: l.reference_image || '',
          reference_image_url: l.reference_image_url || '',
          unit: l.unit || '',
          reference_id: l.reference_id || '',
          is_catalogue: !!l.reference_id,
          uuid: l.uuid || '',
          line_status: l.line_status || 'pending',
          line_note: l.line_note || '',
          line_decision: l.line_decision || 'pending'
        }))
      )
      setBudget(res.budget)
    } catch (error) {
      eventAlert(
        error?.message || 'Failed to load order',
        constants.ERROR,
        'Program Material'
      )
    } finally {
      setLoading(false)
    }
  }, [centreId, monthDate])

  React.useEffect(() => {
    loadOrder()
  }, [loadOrder])

  const liveTotal = React.useMemo(
    () => lines.reduce((sum, l) => sum + num(l.qty) * num(l.unit_cost), 0),
    [lines]
  )

  const updateLine = (index, patch) =>
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)))

  // picked a catalogue item -> auto-fill cost + link + unit. If the catalogue
  // has no link/price for it, those cells become required gap inputs.
  const onLinePick = (index, it) =>
    updateLine(index, {
      item_name: it.name,
      unit_cost: it.cost != null ? String(it.cost) : '',
      reference_link: it.reference_link || '',
      link_from_catalogue: !!it.reference_link,
      reference_image: '',
      reference_image_url: '',
      unit: it.unit || '',
      reference_id: it.reference_id || '',
      is_catalogue: true
    })

  // gap line: upload a photo of the wanted item (alternative to a link)
  const onPickImage = async (index, file) => {
    if (!file) return
    try {
      const res = await uploadItemImage(file)
      updateLine(index, {
        reference_image: res.key || '',
        reference_image_url: res.url || ''
      })
      notify('Image attached', '', 'success')
    } catch (err) {
      // Image storage can be down (e.g. expired storage credentials). Keep the
      // line usable by pointing the centre at the link option instead.
      eventAlert(
        "Couldn't upload the image right now — please paste a link to the item instead.",
        constants.ERROR,
        'Image upload unavailable'
      )
    }
  }

  const addLine = () => setLines((prev) => [...prev, emptyLine()])
  const removeLine = (index) =>
    setLines((prev) => prev.filter((_, i) => i !== index))

  const onUploadCsv = async (e) => {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file) return
    try {
      const data = await parseCsvFile(file)
      const parsed = data
        .map((r) => ({
          item_name: (r.item_name || r['Item'] || '').trim(),
          qty: r.qty ?? r['Qty'] ?? '',
          unit_cost: r.unit_cost ?? r['Unit Cost'] ?? '',
          remarks: r.remarks || r['Remarks'] || '',
          reference_link: '',
          link_from_catalogue: false,
          reference_image: '',
          reference_image_url: '',
          unit: '',
          reference_id: (r.reference_id || r['Reference ID'] || '').trim(),
          is_catalogue: false,
          uuid: '',
          line_status: 'pending',
          line_note: '',
          // raw Line Total from the file (blank = skip check)
          _lineTotal: r.line_total ?? r['Line Total'] ?? ''
        }))
        .filter((l) => l.item_name)
      if (!parsed.length) {
        notify('No rows found in the CSV', '', 'warning')
        return
      }
      // N4: where Line Total is given, it must equal qty × unit_cost — else the
      // whole import is blocked and the offending rows are listed.
      const mismatches = parsed
        .map((l, i) => {
          const declared = l._lineTotal
          if (declared === '' || declared === null || declared === undefined) {
            return null
          }
          const expected = num(l.qty) * num(l.unit_cost)
          const got = num(declared)
          return Math.abs(expected - got) > 0.005
            ? { row: i + 1, item_name: l.item_name, expected, got }
            : null
        })
        .filter(Boolean)
      if (mismatches.length) {
        setBadRows(mismatches)
        return
      }
      const newLines = parsed.map(({ _lineTotal, ...l }) => l)
      setLines((prev) => [...prev.filter((l) => l.item_name), ...newLines])
      notify(`${newLines.length} line(s) added from CSV`, '', 'success')
    } catch (err) {
      eventAlert(
        err?.message || 'Could not read CSV',
        constants.ERROR,
        'Program Material'
      )
    }
  }

  // Template mirrors the on-screen columns/order (Item, Reference ID, Qty,
  // Unit Cost, Line Total, Remarks). Items are catalogue-only, so the Reference
  // ID is what links a row to a catalogue item.
  const downloadTemplate = () =>
    downloadCsv(
      'program-material-template',
      ['Item', 'Reference ID', 'Qty', 'Unit Cost', 'Line Total', 'Remarks'],
      [['Example item', '', 1, 100, 100, '']]
    )

  const save = async (nextStatus) => {
    if (!centreId) {
      notify('Select a centre', '', 'warning')
      return
    }
    const cleanLines = lines
      .filter((l) => l.item_name && num(l.qty) > 0)
      .map((l) => ({
        item_name: l.item_name,
        qty: num(l.qty),
        unit_cost: num(l.unit_cost),
        remarks: l.remarks || '',
        reference_link: l.reference_link || '',
        reference_image: l.reference_image || '',
        unit: l.unit || '',
        reference_id: l.reference_id || ''
      }))
    if (nextStatus === 'submitted' && isEmpty(cleanLines)) {
      notify('Add at least one line before submitting', '', 'warning')
      return
    }
    // Catalogue-only: every submitted line must be a catalogue item (has a
    // reference id). Non-catalogue items are no longer allowed.
    if (nextStatus === 'submitted') {
      const nonCatalogue = cleanLines
        .filter((l) => !(l.reference_id || '').trim())
        .map((l) => l.item_name || 'item')
      if (nonCatalogue.length) {
        notify(
          `Only catalogue items can be ordered. Pick from the catalogue for: ${nonCatalogue
            .slice(0, 5)
            .join(', ')}${nonCatalogue.length > 5 ? '…' : ''}`,
          '',
          'warning'
        )
        return
      }
      // Every line needs a price (the catalogue auto-fills it; the centre fills
      // it when the catalogue has none).
      const noPrice = cleanLines
        .filter((l) => !(num(l.unit_cost) > 0))
        .map((l) => l.item_name || 'item')
      if (noPrice.length) {
        notify(
          `Enter a price for: ${noPrice.slice(0, 5).join(', ')}${
            noPrice.length > 5 ? '…' : ''
          }`,
          '',
          'warning'
        )
        return
      }
      // Every line needs a link OR an image so procurement knows what to buy.
      const noProof = cleanLines
        .filter(
          (l) => !(l.reference_link || '').trim() && !(l.reference_image || '').trim()
        )
        .map((l) => l.item_name || 'item')
      if (noProof.length) {
        notify(
          `Add a link or image for: ${noProof.slice(0, 5).join(', ')}${
            noProof.length > 5 ? '…' : ''
          }`,
          '',
          'warning'
        )
        return
      }
    }
    const doSave = async () => {
      setSaving(true)
      try {
        const res = await saveProgramMaterial({
          centre: centreId,
          month: monthDate,
          status: nextStatus,
          remarks: headerRemarks,
          lines: cleanLines
        })
        setStatus(res.status)
        setBudget(res.budget)
        notify(
          nextStatus === 'submitted' ? 'Order submitted' : 'Draft saved',
          '',
          'success'
        )
        await loadOrder()
        loadHistory()
      } catch (error) {
        const raw = errorText(error, 'Failed to save order')
        const monthLabel = Dayjs(monthDate).format('MMMM YYYY')
        let msg = raw
        if (/locked/i.test(raw)) {
          msg =
            `This order was submitted and is locked for ${monthLabel}. ` +
            'To make changes, please ask the Procurement team to reopen it.'
        } else if (/frozen/i.test(raw)) {
          msg =
            `${monthLabel} has been closed by the Procurement team. ` +
            'Ask them to reopen it if you need to make changes.'
        }
        eventAlert(msg, constants.ERROR, 'Program Material')
      } finally {
        setSaving(false)
      }
    }

    // P5: a single category may overspend — warn but allow. The overall month
    // budget (all categories) is the hard cap.
    const catTotal = cleanLines.reduce(
      (s, l) => s + Number(l.qty) * Number(l.unit_cost),
      0
    )
    // Hard cap: block submit if the month's total across ALL categories would
    // exceed the overall budget (the server enforces this too).
    if (nextStatus === 'submitted') {
      const ob = await checkOverallBudget({
        centre: centreId,
        month: monthDate,
        category: 'program_material',
        projectedCategoryValue: catTotal
      })
      if (ob.exceeded) {
        eventAlert(
          overBudgetMessage(ob, Dayjs(monthDate).format('MMMM YYYY'), 'order'),
          constants.ERROR,
          'Month budget exceeded'
        )
        return
      }
    }
    // Submitting locks the month and sends it to HQ — always confirm first
    // (matches Consumables), folding in the category-overspend note when it
    // applies. A plain draft save never prompts.
    if (nextStatus === 'submitted') {
      const overspent = budget != null && catTotal > Number(budget)
      const msg = overspent
        ? softOverspendNote('program material') +
          ' Submit program material for this month? It will be locked and sent to HQ for approval.'
        : 'Submit program material for this month? It will be locked and sent to HQ for approval.'
      confirm(doSave, msg, () => {})
      return
    }
    await doSave()
  }

  // Open the per-line receipt dialog. On a first receipt every line defaults to
  // "received". On a RESEND (HQ resent only the flagged items), the lines the
  // centre already received keep that status and are locked — the centre only
  // re-confirms the resent lines. A resend is detectable because at least one
  // line is already 'received' while others are back to 'pending'.
  const openReceipt = () => {
    const rows = lines.filter((l) => l.item_name)
    const isResend = rows.some((l) => l.receipt_status === 'received')
    setReceiptLines(
      rows.map((l) => {
        const alreadyReceived = isResend && l.receipt_status === 'received'
        return {
          uuid: l.uuid,
          item_name: l.item_name,
          receipt_status: 'received',
          receipt_note: '',
          alreadyReceived
        }
      })
    )
    setReceipt(true)
  }

  const updateReceiptLine = (i, patch) =>
    setReceiptLines((prev) =>
      prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l))
    )

  // Centre confirms receipt of a 'sent' order, line by line (discrepancy + note
  // on the specific lines that are wrong).
  const submitReceipt = async () => {
    const missingNote = receiptLines.filter(
      (l) => l.receipt_status === 'discrepancy' && !l.receipt_note.trim()
    )
    if (missingNote.length) {
      notify('Add a note for each line marked as a discrepancy', '', 'warning')
      return
    }
    const withIssues = receiptLines.some(
      (l) => l.receipt_status === 'discrepancy' || l.receipt_status === 'not_received'
    )
    setReceiptSaving(true)
    try {
      await confirmReceipt(
        centreId,
        monthDate,
        receiptLines.map((l) => ({
          uuid: l.uuid,
          receipt_status: l.receipt_status,
          receipt_note: l.receipt_note.trim()
        }))
      )
      notify(
        withIssues ? 'Receipt confirmed with issues' : 'Receipt confirmed',
        '',
        'success'
      )
      setReceipt(null)
      setReceiptLines([])
      await loadOrder()
      loadHistory()
    } catch (error) {
      eventAlert(
        error?.message || 'Failed to confirm receipt',
        constants.ERROR,
        'Program Material'
      )
    } finally {
      setReceiptSaving(false)
    }
  }

  return (
    <Paper className={`${classes.root} ${classes.noSpin}`} elevation={0}>
      <div className={classes.toolbar}>
        {/* The Center-Team tab already names the page; only show a title in the
            standalone (uncontrolled) view to avoid a redundant repeated name. */}
        {!controlled && <Typography variant="h6">Program Material</Typography>}
        {!controlled && (
          <TextField
            select
            className={classes.select}
            size="small"
            variant="outlined"
            label="Centre"
            value={centreId}
            onChange={(e) => setCentreId(e.target.value)}>
            {centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          type="month"
          label="Month"
          size="small"
          variant="outlined"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant="body2" color="textSecondary">
          Status:{' '}
          <span className={classes.status}>
            {ORDER_STATUS_LABEL[status] || status}
          </span>
          {/* PO number is HQ-only — not shown in the centre view. */}
        </Typography>
        {/* Kept on the left (next to status) so it isn't hidden off the right
            edge when the toolbar is crowded. */}
        {status === 'sent' && (
          <Button variant="contained" color="primary" onClick={openReceipt}>
            Check &amp; confirm receipt
          </Button>
        )}
        <div className={classes.spacer} />
        {!locked && (
          <>
            {SHOW_CSV_IMPORT && (
              <>
                <Button component="label" size="small" variant="outlined">
                  Upload CSV
                  <input
                    type="file"
                    hidden
                    accept=".csv"
                    onChange={onUploadCsv}
                  />
                </Button>
                <Button size="small" onClick={downloadTemplate}>
                  Template
                </Button>
              </>
            )}
            <SopButton category="program_material" />
            <Button
              variant="outlined"
              color="primary"
              disabled={saving || loading}
              onClick={() => save('draft')}>
              Save draft
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={saving || loading}
              onClick={() => save('submitted')}>
              {saving ? 'Saving…' : 'Submit'}
            </Button>
          </>
        )}
      </div>

      <BudgetBanner
        label="This month"
        budget={budget}
        used={liveTotal}
        remaining={
          budget === null || budget === undefined
            ? null
            : Number(budget) - liveTotal
        }
      />

      {status === 'rejected' && reviewRemarks ? (
        <Box
          mb={2}
          p={1.5}
          style={{ background: '#ffebee', borderRadius: 8, color: '#c62828' }}>
          <strong>Rejected by HQ:</strong> {reviewRemarks}
        </Box>
      ) : null}

      {status === 'sent' ? (
        <Box
          mb={2}
          p={1.5}
          style={{ background: '#fff8e1', borderRadius: 8, color: '#8d6e00' }}>
          HQ has sent this order to your centre. When the items arrive, click{' '}
          <strong>Check &amp; confirm receipt</strong> at the top and mark each item{' '}
          <strong>Received</strong>, <strong>Not received</strong> (never
          arrived), or <strong>Received with discrepancy</strong> (arrived
          short, damaged or wrong — add a note and a photo).
        </Box>
      ) : null}

      {status === 'received' ? (
        <Box
          mb={2}
          p={1.5}
          style={{
            background: discrepancy ? '#ffebee' : '#e8f5e9',
            borderRadius: 8,
            color: discrepancy ? '#c62828' : '#2e7d32'
          }}>
          {discrepancy ? (
            <>
              <strong>Received with issues:</strong>{' '}
              {discrepancyNotes || '—'}
            </>
          ) : (
            <strong>Received — no issues reported.</strong>
          )}
        </Box>
      ) : null}

      {monthFrozen ? (
        <Box
          mb={2}
          p={1.5}
          style={{ background: '#ede7f6', borderRadius: 8, color: '#5e35b1' }}>
          This month has been <strong>frozen by HQ</strong> — program material can
          no longer be edited until HQ unfreezes it.
        </Box>
      ) : null}

      {locked && !monthFrozen && status !== 'sent' && status !== 'received' ? (
        <Box mb={2} p={1.5} style={{ background: '#eceff1', borderRadius: 8 }}>
          <Typography variant="body2" color="textSecondary">
            This order is <strong>{status}</strong> and is now locked for editing.
            {status === 'submitted'
              ? ' It’s with HQ for review.'
              : ' HQ has acted on it.'}{' '}
            Once submitted, a program-material order can only be changed after HQ
            reopens it — <strong>ask HQ to reopen it for editing</strong> if you
            need to make changes.
          </Typography>
        </Box>
      ) : null}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        // Scroll the wide table inside its own box so it doesn't stretch the page
        // (which pushed Save draft / Submit off-screen). The catalogue dropdown
        // renders in a Popper, so horizontal scroll can't clip it.
        <TableContainer style={{ overflow: 'visible' }}>
          <Table size="small" className={classes.stickyHead}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 40 }}>Sl No.</TableCell>
                <TableCell style={{ minWidth: 200 }}>Item</TableCell>
                <TableCell style={{ width: 150 }}>Ref ID</TableCell>
                <TableCell style={{ width: 170 }}>Item link / image</TableCell>
                <TableCell align="right" style={{ width: 64 }}>
                  Qty
                </TableCell>
                <TableCell align="right" style={{ width: 88 }}>
                  Unit Cost (₹)
                </TableCell>
                <TableCell align="right" style={{ width: 88 }}>
                  Line Total (₹)
                </TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell align="center" style={{ width: 64 }}>
                  Remove
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, index) => (
                <TableRow key={index}>
                  <TableCell style={{ verticalAlign: 'top' }}>
                    {index + 1}
                  </TableCell>
                  <TableCell style={{ verticalAlign: 'top' }}>
                    {/* Catalogue-only: every item is picked from the catalogue. */}
                    <CatalogueSearch
                      line={line}
                      disabled={locked}
                      onPick={(it) => onLinePick(index, it)}
                    />
                    {line.line_decision === 'rejected' ? (
                      <div>
                        <Chip
                          size="small"
                          className={classes.lineChip}
                          label="Rejected by HQ"
                          style={{ background: '#ffebee', color: '#c62828' }}
                        />
                      </div>
                    ) : null}
                    {line.line_status && line.line_status !== 'pending' ? (
                      <div>
                        <Chip
                          size="small"
                          className={classes.lineChip}
                          label={
                            LINE_STATUS_LABEL[line.line_status] ||
                            line.line_status
                          }
                          style={LINE_STATUS_STYLE[line.line_status]}
                        />
                        {line.line_note ? (
                          <div className={classes.lineNote}>{line.line_note}</div>
                        ) : null}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell style={{ verticalAlign: 'top' }}>
                    {line.reference_id ? (
                      <span style={{ fontSize: 12 }}>{line.reference_id}</span>
                    ) : (
                      <span style={{ fontSize: 12, color: '#999' }}>—</span>
                    )}
                  </TableCell>
                  <TableCell style={{ verticalAlign: 'top' }}>
                    {line.link_from_catalogue && line.reference_link ? (
                      // Catalogue already has a link — read-only, one line.
                      <Tooltip title="Link from catalogue">
                        <a
                          href={line.reference_link}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 12 }}>
                          view ↗
                        </a>
                      </Tooltip>
                    ) : (
                      // Gap item: a link OR an image. The whole cell is a drop
                      // target so an image can be dragged onto it.
                      <div
                        onDragOver={(e) => {
                          if (!locked) e.preventDefault()
                        }}
                        onDrop={(e) => {
                          if (locked) return
                          e.preventDefault()
                          const f =
                            e.dataTransfer &&
                            e.dataTransfer.files &&
                            e.dataTransfer.files[0]
                          if (f) onPickImage(index, f)
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder="Link"
                          disabled={locked}
                          // Red outline until a link or image is supplied.
                          error={
                            !!line.item_name &&
                            !(line.reference_link || '').trim() &&
                            !(line.reference_image || '').trim()
                          }
                          style={{ width: 96 }}
                          value={line.reference_link}
                          onChange={(e) =>
                            updateLine(index, { reference_link: e.target.value })
                          }
                        />
                        <span style={{ color: '#bbb' }}>/</span>
                        {line.reference_image ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              fontSize: 12
                            }}>
                            <a
                              href={line.reference_image_url}
                              target="_blank"
                              rel="noreferrer">
                              image ↗
                            </a>
                            {!locked && (
                              <IconButton
                                size="small"
                                title="Remove image"
                                onClick={() =>
                                  updateLine(index, {
                                    reference_image: '',
                                    reference_image_url: ''
                                  })
                                }>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            )}
                          </span>
                        ) : (
                          <Tooltip title="Click or drag an image here">
                            <IconButton
                              size="small"
                              component="label"
                              disabled={locked}>
                              <ImageOutlinedIcon fontSize="small" />
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => {
                                  const f = e.target.files && e.target.files[0]
                                  e.target.value = ''
                                  onPickImage(index, f)
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell align="right" style={{ verticalAlign: 'top' }}>
                    <TextField
                      size="small"
                      variant="outlined"
                      type="number"
                      disabled={locked}
                      style={{ width: 60 }}
                      value={line.qty}
                      onChange={(e) =>
                        updateLine(index, { qty: e.target.value })
                      }
                      inputProps={{ min: 0, style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell align="right" style={{ verticalAlign: 'top' }}>
                    <TextField
                      size="small"
                      variant="outlined"
                      type="number"
                      disabled={locked}
                      // Flag a missing price (the catalogue had none) so the
                      // centre fills it before submitting.
                      error={line.item_name && !(num(line.unit_cost) > 0)}
                      style={{ width: 82 }}
                      value={line.unit_cost}
                      onChange={(e) =>
                        updateLine(index, { unit_cost: e.target.value })
                      }
                      inputProps={{ min: 0, style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell align="right" style={{ verticalAlign: 'top' }}>
                    {(num(line.qty) * num(line.unit_cost)).toLocaleString(
                      'en-IN'
                    )}
                  </TableCell>
                  <TableCell style={{ verticalAlign: 'top' }}>
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="note"
                      disabled={locked}
                      style={{ width: 140 }}
                      value={line.remarks}
                      onChange={(e) =>
                        updateLine(index, { remarks: e.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell align="center" style={{ verticalAlign: 'top' }}>
                    <IconButton
                      size="small"
                      disabled={locked}
                      onClick={() => removeLine(index)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {isEmpty(lines) && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="textSecondary">
                      No lines yet — add one below.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box mt={2} display="flex" alignItems="center">
        {!locked && (
          <Button startIcon={<AddIcon />} onClick={addLine}>
            Add line
          </Button>
        )}
        <div className={classes.spacer} />
        <Typography className={classes.total}>
          Total: ₹{liveTotal.toLocaleString('en-IN')}
        </Typography>
      </Box>

      <Box mt={4}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Past orders
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Items</TableCell>
              <TableCell align="right">Total (₹)</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((h) => (
              <TableRow key={h.month} hover>
                <TableCell>{Dayjs(h.month).format('MMMM YYYY')}</TableCell>
                <TableCell style={{ textTransform: 'lowercase' }}>
                  {h.status}
                </TableCell>
                <TableCell align="right">{h.items}</TableCell>
                <TableCell align="right">
                  {Number(h.total).toLocaleString('en-IN')}
                </TableCell>
                <TableCell align="center">
                  <Button size="small" onClick={() => openView(h.month)}>
                    View
                  </Button>
                  {/* Edit only when the order is still editable (draft/rejected);
                      submitted/approved and frozen months are View-only — an Edit
                      button there just opened a locked form (#6). */}
                  {['draft', 'rejected'].includes(h.status) && (
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => setMonth(h.month.slice(0, 7))}>
                      Edit
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {isEmpty(history) && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="textSecondary">
                    No past orders.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <Dialog
        open={!!viewOrder}
        onClose={() => setViewOrder(null)}
        maxWidth="md"
        fullWidth>
        <DialogTitle>
          Program Material —{' '}
          {viewOrder ? Dayjs(viewOrder.month).format('MMMM YYYY') : ''}{' '}
          <span style={{ textTransform: 'lowercase', color: '#888' }}>
            ({viewOrder?.status})
          </span>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Unit Cost (₹)</TableCell>
                <TableCell align="right">Line Total (₹)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(viewOrder?.lines || []).map((l, i) => (
                <TableRow key={i}>
                  <TableCell>
                    {l.item_name}
                    {l.reference_link ? (
                      <a
                        href={l.reference_link}
                        target="_blank"
                        rel="noreferrer"
                        style={{ marginLeft: 8, fontSize: 12 }}>
                        view ↗
                      </a>
                    ) : null}
                    {l.reference_image_url ? (
                      <a
                        href={l.reference_image_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ marginLeft: 8, fontSize: 12 }}>
                        image ↗
                      </a>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">{l.qty}</TableCell>
                  <TableCell align="right">{l.unit_cost}</TableCell>
                  <TableCell align="right">
                    {(Number(l.qty) * Number(l.unit_cost)).toLocaleString(
                      'en-IN'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      <Dialog open={!!receipt} onClose={() => setReceipt(null)} maxWidth="md" fullWidth>
        <DialogTitle>Check &amp; confirm receipt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Mark each item <strong>Received</strong>, <strong>Not received</strong>{' '}
            (it never arrived), or <strong>Received with discrepancy</strong>{' '}
            (short, damaged, wrong item…) with a note and a photo. Not-received
            and discrepancy items are flagged to HQ per line.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell style={{ width: 150 }}>Receipt</TableCell>
                <TableCell>Note (if discrepancy)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {receiptLines.map((l, i) => (
                <TableRow key={l.uuid || i}>
                  <TableCell>
                    {l.item_name}
                    {l.alreadyReceived && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        already received
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      fullWidth
                      // On a resend, lines received the first time are locked so
                      // the centre only re-confirms the resent (previously
                      // flagged) items.
                      disabled={l.alreadyReceived}
                      value={l.receipt_status}
                      onChange={(e) =>
                        updateReceiptLine(i, { receipt_status: e.target.value })
                      }>
                      <MenuItem value="received">Received</MenuItem>
                      <MenuItem value="not_received">Not received</MenuItem>
                      <MenuItem value="discrepancy">
                        Received with discrepancy
                      </MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      placeholder="What's wrong?"
                      disabled={l.receipt_status !== 'discrepancy'}
                      value={l.receipt_note}
                      onChange={(e) =>
                        updateReceiptLine(i, { receipt_note: e.target.value })
                      }
                    />
                    {l.receipt_status === 'discrepancy' && l.uuid ? (
                      <Box mt={1}>
                        <AttachmentUploader
                          entityType="pm_line"
                          line={l.uuid}
                          label="Photo of the item"
                        />
                      </Box>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceipt(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={receiptSaving}
            onClick={submitReceipt}>
            {receiptSaving ? 'Saving…' : 'Confirm receipt'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!badRows} onClose={() => setBadRows(null)} maxWidth="sm" fullWidth>
        <DialogTitle>CSV not imported — Line Total mismatch</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            These rows have a <strong>Line Total</strong> that doesn&apos;t equal
            Qty × Unit Cost. Fix them in the file and upload again — nothing was
            imported.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Row</TableCell>
                <TableCell>Item</TableCell>
                <TableCell align="right">Expected (Qty×Cost)</TableCell>
                <TableCell align="right">Line Total in file</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(badRows || []).map((b) => (
                <TableRow key={b.row}>
                  <TableCell>{b.row}</TableCell>
                  <TableCell>{b.item_name}</TableCell>
                  <TableCell align="right">
                    {b.expected.toLocaleString('en-IN')}
                  </TableCell>
                  <TableCell align="right" style={{ color: '#c62828' }}>
                    {b.got.toLocaleString('en-IN')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBadRows(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default ProgramMaterial
